import React, { useMemo, useState, useRef } from "react";
import "./AdminCohort.scss";

const AdminDualListBox = ({
  allUsers = [],
  currentIds = [],
  onChange,
  leftTitle = "Current users",
  rightTitle = "Potential users",
  disabled = false,
}) => {
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");
  const [leftSelected, setLeftSelected] = useState([]);
  const [rightSelected, setRightSelected] = useState([]);
  const lastClicked = useRef({ left: null, right: null });

  const usersById = useMemo(() => {
    const map = new Map();
    allUsers.forEach((u) => map.set(u.id, u));
    return map;
  }, [allUsers]);

  const currentSet = useMemo(() => new Set(currentIds), [currentIds]);

  const currentUsers = useMemo(
    () => currentIds.map((id) => usersById.get(id)).filter(Boolean),
    [currentIds, usersById]
  );

  const potentialUsers = useMemo(
    () => allUsers.filter((u) => !currentSet.has(u.id)),
    [allUsers, currentSet]
  );

  const filterUsers = (list, term) => {
    if (!term.trim()) return list;
    const q = term.trim().toLowerCase();
    return list.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
    );
  };

  const filteredLeft = useMemo(
    () => filterUsers(currentUsers, leftSearch),
    [currentUsers, leftSearch]
  );
  const filteredRight = useMemo(
    () => filterUsers(potentialUsers, rightSearch),
    [potentialUsers, rightSearch]
  );

  const handleRowClick = (side, id, index, e) => {
    if (disabled) return;
    const list = side === "left" ? filteredLeft : filteredRight;
    const setSelected = side === "left" ? setLeftSelected : setRightSelected;
    const selected = side === "left" ? leftSelected : rightSelected;

    if (e.shiftKey && lastClicked.current[side] !== null) {
      const start = Math.min(lastClicked.current[side], index);
      const end = Math.max(lastClicked.current[side], index);
      const rangeIds = list.slice(start, end + 1).map((u) => u.id);
      setSelected(Array.from(new Set([...selected, ...rangeIds])));
    } else if (e.ctrlKey || e.metaKey) {
      setSelected(
        selected.includes(id)
          ? selected.filter((sid) => sid !== id)
          : [...selected, id]
      );
      lastClicked.current[side] = index;
    } else {
      setSelected([id]);
      lastClicked.current[side] = index;
    }
  };

  // Toggle a single row's checkbox without affecting shift/ctrl click semantics.
  const handleCheckboxToggle = (side, id, index, e) => {
    e.stopPropagation();
    if (disabled) return;
    const setSelected = side === "left" ? setLeftSelected : setRightSelected;
    const selected = side === "left" ? leftSelected : rightSelected;

    setSelected(
      selected.includes(id)
        ? selected.filter((sid) => sid !== id)
        : [...selected, id]
    );
    lastClicked.current[side] = index;
  };

  // Select-all / deselect-all checkbox for a panel (applies to the currently filtered list).
  const handleSelectAllToggle = (side) => {
    if (disabled) return;
    const list = side === "left" ? filteredLeft : filteredRight;
    const selected = side === "left" ? leftSelected : rightSelected;
    const setSelected = side === "left" ? setLeftSelected : setRightSelected;
    const listIds = list.map((u) => u.id);
    const allSelected =
      listIds.length > 0 && listIds.every((id) => selected.includes(id));

    setSelected(allSelected ? [] : listIds);
  };

  const moveToLeft = (ids) => {
    if (disabled || !ids.length) return;
    const next = Array.from(new Set([...currentIds, ...ids]));
    onChange?.(next);
    setRightSelected([]);
  };

  const moveToRight = (ids) => {
    if (disabled || !ids.length) return;
    const remove = new Set(ids);
    const next = currentIds.filter((id) => !remove.has(id));
    onChange?.(next);
    setLeftSelected([]);
  };

  const handleDragStart = (side, id, e) => {
    if (disabled) return;
    const selected = side === "left" ? leftSelected : rightSelected;
    const ids = selected.includes(id) ? selected : [id];
    e.dataTransfer.setData("text/plain", JSON.stringify({ side, ids }));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (targetSide, e) => {
    e.preventDefault();
    if (disabled) return;
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }
    if (!payload || payload.side === targetSide) return;
    if (targetSide === "left") moveToLeft(payload.ids);
    else moveToRight(payload.ids);
  };

  const renderList = (side, list, selected) => (
    <div
      className="dlb-panel-list"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => handleDrop(side, e)}
    >
      {list.length === 0 ? (
        <div className="dlb-empty">None</div>
      ) : (
        list.map((u, index) => (
          <div
            key={u.id}
            className={`dlb-row${selected.includes(u.id) ? " is-selected" : ""}`}
            draggable={!disabled}
            onDragStart={(e) => handleDragStart(side, u.id, e)}
            onClick={(e) => handleRowClick(side, u.id, index, e)}
            onDoubleClick={() =>
              side === "left" ? moveToRight([u.id]) : moveToLeft([u.id])
            }
            title={u.email}
          >
            <input
              type="checkbox"
              className="dlb-row-checkbox"
              checked={selected.includes(u.id)}
              disabled={disabled}
              onChange={(e) => handleCheckboxToggle(side, u.id, index, e)}
              onClick={(e) => e.stopPropagation()}
            />
            <span className="dlb-row-name">{u.name}</span>
            {u.email && <span className="dlb-row-email">{u.email}</span>}
          </div>
        ))
      )}
    </div>
  );

  const renderPanelHeader = (side, title, list, selected) => {
    const listIds = list.map((u) => u.id);
    const allSelected =
      listIds.length > 0 && listIds.every((id) => selected.includes(id));
    const someSelected = listIds.some((id) => selected.includes(id));

    return (
      <div className="dlb-panel-title">
        <label className="dlb-select-all">
          <input
            type="checkbox"
            className="mx-3"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = !allSelected && someSelected;
            }}
            disabled={disabled || listIds.length === 0}
            onChange={() => handleSelectAllToggle(side)}
          />
          <span className="mx-6">{title}</span>
        </label>
      </div>
    );
  };

  return (
    <div className="dual-list-box">
      <div className="dlb-panel">
        {renderPanelHeader("left", leftTitle, filteredLeft, leftSelected)}
        {renderList("left", filteredLeft, leftSelected)}
        <div className="dlb-search-row">
          <input
            type="text"
            placeholder="Search"
            value={leftSearch}
            disabled={disabled}
            onChange={(e) => setLeftSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn-clear"
            disabled={disabled}
            onClick={() => setLeftSearch("")}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="dlb-controls">
        <button
          type="button"
          className="btn-move"
          disabled={disabled || rightSelected.length === 0}
          onClick={() => moveToLeft(rightSelected)}
        >
          ◄ Add
        </button>
        <button
          type="button"
          className="btn-move"
          disabled={disabled || leftSelected.length === 0}
          onClick={() => moveToRight(leftSelected)}
        >
          Remove ►
        </button>
      </div>

      <div className="dlb-panel">
        {renderPanelHeader(
          "right",
          `${rightTitle} (${potentialUsers.length})`,
          filteredRight,
          rightSelected
        )}
        {renderList("right", filteredRight, rightSelected)}
        <div className="dlb-search-row">
          <input
            type="text"
            placeholder="Search"
            value={rightSearch}
            disabled={disabled}
            onChange={(e) => setRightSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn-clear"
            disabled={disabled}
            onClick={() => setRightSearch("")}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDualListBox;